import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Lock, ShieldCheck, CreditCard, Apple, Smartphone, Loader2, CheckCircle2, Tag, ChevronDown, ChevronUp } from 'lucide-react';
import { createPaymentIntent, getStripe, redeemPromoCode } from '../services/stripeService';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../hooks/useCurrency';
import { getPlan, formatPrice, type PlanId } from '../lib/pricing';
import CurrencyPicker from '../components/CurrencyPicker';
import type { User } from '../types';

// Plan-specific metadata that isn't part of the price table (credit counts
// for the auth context, whether the plan is recurring, pretty description).
// Amounts come from lib/pricing.ts so Pricing and Checkout always agree.
const PLAN_META: Record<PlanId, { label: string; credits: number; plan: User['plan']; recurring: boolean }> = {
  try:     { label: 'Try pack — 5 credits',            credits: 5,    plan: 'try',     recurring: false },
  starter: { label: 'Starter credit pack',             credits: 20,   plan: 'starter', recurring: false },
  pro:     { label: 'Pro subscription — 200 ops/mo',   credits: 200,  plan: 'pro',     recurring: true  },
  team:    { label: 'Team subscription — 1000 ops/mo', credits: 1000, plan: 'team',    recurring: true  },
};

export default function Checkout() {
  const { plan = 'try' } = useParams<{ plan: PlanId }>();
  const safePlan: PlanId = (plan in PLAN_META ? plan : 'try') as PlanId;
  const { currency } = useCurrency();
  const planDef = getPlan(safePlan);
  const meta = PLAN_META[safePlan];
  const details = {
    name: meta.label,
    price: planDef.amounts[currency],
    currency,
    credits: meta.credits,
    plan: meta.plan,
    recurring: meta.recurring,
  };
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripe, setStripe] = useState<Awaited<ReturnType<typeof getStripe>> | null>(null);
  // `paymentStatus` replaces the old boolean `success`. We need to distinguish
  // between a synchronous-confirmed payment (cards, wallets — status:'succeeded')
  // and an async pending payment (PayNow, UPI bank redirect, WeChat — status:
  // 'processing' or 'requires_action'). The latter must NOT claim credits have
  // landed, because the webhook hasn't fired yet and the backend hasn't granted
  // anything. See bug fix commit: previously we treated "no error from
  // confirmPayment" as success, which lied to PayNow users who had only scanned
  // the QR (and may never have completed the bank-app authorisation).
  const [paymentStatus, setPaymentStatus] = useState<'succeeded' | 'processing' | null>(null);
  // initError surfaces backend/Stripe failures that would otherwise leave the
  // "Preparing secure checkout…" spinner stuck forever. Shown inline on the
  // card and as a toast so the user isn't blocked without explanation.
  const [initError, setInitError] = useState<string | null>(null);
  const { user, addCredits } = useAuth();
  const isMock = !stripe;

  // Re-run whenever plan OR currency changes — switching currency mid-session
  // needs a fresh PaymentIntent because Stripe bakes currency into the intent
  // at creation time (you can't reuse a USD intent to charge INR).
  useEffect(() => {
    let cancelled = false;
    setClientSecret(null);
    setInitError(null);
    (async () => {
      try {
        const s = await getStripe();
        if (cancelled) return;
        setStripe(s);
        const intent = await createPaymentIntent({ planId: safePlan, currency });
        if (cancelled) return;
        if (!intent?.clientSecret) {
          throw new Error('Server did not return a clientSecret — check Stripe configuration on the API.');
        }
        setClientSecret(intent.clientSecret);
      } catch (err: any) {
        if (cancelled) return;
        const msg = err?.response?.data?.error
          ?? err?.response?.data?.message
          ?? err?.message
          ?? 'Unable to initialise checkout.';
        setInitError(msg);
        toast.error(msg);
      }
    })();
    return () => { cancelled = true; };
  }, [safePlan, currency]);

  // Mock-only path: no Stripe key configured, demo flow. Grant credits
  // optimistically because there is no webhook coming.
  const finalizeMockPayment = () => {
    addCredits(details.credits, details.plan);
    setPaymentStatus('succeeded');
    toast.success('Payment successful');
  };

  // Real Stripe path: NEVER grant credits client-side. The Stripe webhook on
  // payment_intent.succeeded is the single source of truth — it writes the
  // increment via Admin SDK (bypassing Firestore rules), and the AuthProvider's
  // onSnapshot listener on users/{uid} reflects the new balance within ~1s.
  // For async methods (PayNow QR, UPI, WeChat, Alipay), confirmPayment can
  // resolve without an error while the PaymentIntent is still 'processing' or
  // 'requires_action' — that's a *pending* payment, not a successful one. We
  // route those into a "we're waiting for your bank" screen so we never claim
  // credits have landed when they haven't.
  const finalizeStripePayment = (status: 'succeeded' | 'processing') => {
    setPaymentStatus(status);
    toast.success(status === 'succeeded' ? 'Payment confirmed' : 'Payment received — confirming with your bank');
  };

  // ----- Promo code redemption (100%-off only; non-Stripe path) -------------
  // Why this lives at the page level (not inside StripePaymentForm): a 100%-off
  // promo can be redeemed BEFORE Stripe has loaded — no PaymentIntent needed.
  // We POST /payments/redeem; the backend bypasses Stripe, credits the user
  // atomically, and returns { ok, credits }. Like the Stripe path, we don't
  // optimistically addCredits — the onSnapshot listener on users/{uid} will
  // reflect the new balance within ~1s.
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoSubmitting, setPromoSubmitting] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);

  const handleRedeemPromo = async () => {
    setPromoSubmitting(true);
    setPromoError(null);
    try {
      const res = await redeemPromoCode({ planId: safePlan, promotionCode: promoCode.trim() });
      // Reuse the "succeeded" success screen — copy is identical from the user's
      // perspective ("credits will appear in your account momentarily").
      setPaymentStatus('succeeded');
      toast.success(`Redeemed ${res.code} · +${res.credits} credits`);
    } catch (err: any) {
      // The backend returns precise 4xx messages (invalid / expired / exhausted
      // / wrong plan / already redeemed). Surface them verbatim so users know
      // exactly why and don't waste a code by guessing.
      const msg = err?.response?.data?.error
        ?? err?.message
        ?? 'Unable to redeem this code.';
      setPromoError(msg);
    } finally {
      setPromoSubmitting(false);
    }
  };

  if (paymentStatus === 'succeeded') {
    return (
      <div className="container-narrow py-16 text-center">
        <div className="card p-10 max-w-lg mx-auto">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
          <h2 className="mt-4 text-2xl font-display font-bold">You're all set, {user?.name?.split(' ')[0]}!</h2>
          <p className="text-ink-600 mt-2">
            {/* For the real-Stripe path the local user state hasn't been
                optimistically incremented; the Firestore onSnapshot listener
                will update it once the webhook lands. Phrasing reflects that
                so we don't lie about timing. The mock path mutates state
                immediately, but the same wording is still accurate there. */}
            {details.credits} credits will appear in your account momentarily.
          </p>
          {/* Repeated on the success screen because card statements often
              surface hours or days later — by then the user has forgotten the
              pre-pay notice. This is the last line of defence against "I don't
              recognise this charge" support tickets. */}
          <p className="mt-3 text-xs text-ink-500">
            Your statement will show <strong className="text-ink-700">PRIME AXIS</strong> — that's GradeLab's payments partner.
          </p>
          <div className="mt-6 flex gap-2 justify-center">
            <Link to="/dashboard" className="btn-primary">Go to dashboard</Link>
            <Link to="/evaluate" className="btn-secondary">Evaluate a report</Link>
          </div>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'processing') {
    return (
      <div className="container-narrow py-16 text-center">
        <div className="card p-10 max-w-lg mx-auto">
          <Loader2 className="w-12 h-12 text-brand-500 mx-auto animate-spin" />
          <h2 className="mt-4 text-2xl font-display font-bold">Waiting for your bank…</h2>
          <p className="text-ink-600 mt-2">
            We've received your payment instruction. Your {details.credits} credits will appear
            as soon as your bank confirms — usually within a minute. You can safely leave this page.
          </p>
          <p className="mt-3 text-xs text-ink-500">
            Your statement will show <strong className="text-ink-700">PRIME AXIS</strong> — that's GradeLab's payments partner.
          </p>
          <div className="mt-6 flex gap-2 justify-center">
            <Link to="/dashboard" className="btn-primary">Go to dashboard</Link>
            <Link to="/billing" className="btn-secondary">View billing history</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-narrow py-10">
      <h1 className="text-3xl font-display font-bold">Checkout</h1>
      <p className="text-ink-600 mt-1">You're purchasing <strong>{details.name}</strong>.</p>

      <div className="mt-6 grid md:grid-cols-5 gap-6">
        <div className="md:col-span-3 space-y-4">
          {/* Promo code expander. Lives above the payment form so users see it
              before filling in card details — used for QA test codes and any
              future discount campaigns. The backend handles 100%-off codes
              inline (no Stripe round-trip); for partial codes we'd extend
              createPaymentIntent to accept a code and discount the amount
              before creating the PaymentIntent. */}
          <div className="card p-4">
            <button
              type="button"
              onClick={() => setPromoOpen((v) => !v)}
              className="w-full flex items-center justify-between text-left text-sm font-medium text-ink-800 hover:text-brand-700 transition-colors"
            >
              <span className="flex items-center gap-2">
                <Tag className="w-4 h-4" /> Have a promo code?
              </span>
              {promoOpen ? <ChevronUp className="w-4 h-4 text-ink-400" /> : <ChevronDown className="w-4 h-4 text-ink-400" />}
            </button>
            {promoOpen && (
              <div className="mt-3 space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => { setPromoCode(e.target.value); setPromoError(null); }}
                    placeholder="Enter promo code"
                    className="input flex-1 uppercase tracking-wider"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    disabled={promoSubmitting}
                  />
                  <button
                    type="button"
                    onClick={handleRedeemPromo}
                    disabled={promoSubmitting || !promoCode.trim()}
                    className="btn-primary px-4 whitespace-nowrap"
                  >
                    {promoSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Redeem'}
                  </button>
                </div>
                {promoError && <p className="text-xs text-red-600">{promoError}</p>}
                <p className="text-xs text-ink-500">
                  100%-off codes redeem instantly — no card needed.
                </p>
              </div>
            )}
          </div>

          <div className="card p-6">
          {!clientSecret ? (
            <div className="p-10 text-center text-ink-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" /> Preparing secure checkout…
            </div>
          ) : isMock ? (
            // Production must NEVER fall through to the mock — it's a credits-
            // for-free bug waiting to happen (see the PayNow incident:
            // VITE_STRIPE_PUBLISHABLE_KEY was blank, getStripe() returned null,
            // MockPaymentForm rendered, the user "paid" a fake QR and addCredits
            // ran locally). In production we hard-fail with a clear message so
            // the misconfiguration is obvious instead of silently granting
            // phantom credits. Dev keeps the mock so UI work on /checkout
            // doesn't require a real Stripe key.
            import.meta.env.DEV ? (
              <MockPaymentForm onSuccess={finalizeMockPayment} price={details.price} currency={details.currency} />
            ) : (
              <div className="p-10 text-center text-ink-600">
                <p className="font-semibold text-ink-900">Checkout is temporarily unavailable.</p>
                <p className="mt-2 text-sm">
                  Our payments provider isn't configured on this build. Please refresh, or
                  contact <a className="underline" href="mailto:support@gradelab.club">support@gradelab.club</a> if the issue persists.
                </p>
              </div>
            )
          ) : (
            <Elements stripe={stripe} options={{ clientSecret, appearance: { theme: 'stripe', variables: { colorPrimary: '#4f46e5', borderRadius: '12px' } } }}>
              <StripePaymentForm onResult={finalizeStripePayment} />
            </Elements>
          )}
          </div>
        </div>

        <aside className="md:col-span-2 card p-6 self-start">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Order summary</h3>
            <CurrencyPicker />
          </div>
          <div className="mt-4 flex justify-between text-sm">
            <span className="text-ink-600">{details.name}</span>
            <span className="font-medium">{formatPrice(details.price, details.currency)}</span>
          </div>
          <div className="mt-1 text-xs text-ink-500">
            {details.recurring ? 'Billed monthly. Cancel anytime.' : 'One-time purchase.'}
          </div>
          <div className="border-t border-ink-100 mt-4 pt-4 flex justify-between font-semibold">
            <span>Total</span>
            <span>{formatPrice(details.price, details.currency)} {details.currency.toUpperCase()}</span>
          </div>

          <div className="mt-6 space-y-2 text-xs text-ink-500">
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Payment secured by Stripe</div>
            <div className="flex items-center gap-2"><Lock className="w-4 h-4" /> PCI-DSS compliant, never stored on our servers</div>
          </div>
          <div className="mt-4 flex gap-3 text-ink-400">
            <CreditCard className="w-5 h-5" /> <Apple className="w-5 h-5" /> <Smartphone className="w-5 h-5" />
          </div>
          <p className="mt-2 text-xs text-ink-500">Cards, Apple Pay, Google Pay, WeChat Pay</p>

          {/* Statement-descriptor heads-up. GradeLab's Stripe account bills under
              our payments agency "PRIME AXIS" — telling users up front prevents
              "I don't recognise this charge" chargebacks, which are expensive
              and count against the account's dispute ratio. */}
          <div className="mt-4 p-3 rounded-lg bg-ink-50 border border-ink-100 text-xs text-ink-600 leading-relaxed">
            <strong className="text-ink-800">Heads up:</strong> this charge will appear on
            your statement as <strong className="text-ink-800">PRIME AXIS</strong>, our
            payments partner. That's still GradeLab — save this note so you recognise it
            later.
          </div>
        </aside>
      </div>
    </div>
  );
}

function StripePaymentForm({ onResult }: { onResult: (status: 'succeeded' | 'processing') => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true); setError(null);
    // `redirect: 'if_required'` keeps card/wallet flows inline; async methods
    // (PayNow QR, UPI, WeChat, Alipay) still confirm via stripe.js without a
    // page redirect, but the returned PaymentIntent will be in 'processing' or
    // 'requires_action' rather than 'succeeded' until the bank confirms.
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.origin + '/dashboard' },
      redirect: 'if_required',
    });
    if (error) {
      setError(error.message ?? 'Payment failed');
    } else if (paymentIntent?.status === 'succeeded') {
      // Card / wallet path. Webhook will fire within seconds.
      onResult('succeeded');
    } else if (paymentIntent?.status === 'processing' || paymentIntent?.status === 'requires_action') {
      // Async / pending path (PayNow, UPI, etc.). Show a "waiting for bank"
      // screen instead of lying about credits being granted. The webhook will
      // fire on the bank's confirmation and the onSnapshot listener will
      // surface the new balance live.
      onResult('processing');
    } else {
      // Any other terminal status (requires_payment_method, canceled, etc.) —
      // treat as not-completed so the user can retry instead of seeing a
      // spurious success screen.
      setError(`Payment did not complete (status: ${paymentIntent?.status ?? 'unknown'}). Please try again.`);
    }
    setLoading(false);
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <PaymentElement options={{ layout: 'accordion', wallets: { applePay: 'auto', googlePay: 'auto' } }} />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button type="submit" disabled={!stripe || loading} className="btn-primary w-full py-3">
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</> : <><Lock className="w-4 h-4" /> Pay now</>}
      </button>
    </form>
  );
}

/** Fallback UI used when Stripe keys are not configured — lets the demo flow complete. */
function MockPaymentForm({ onSuccess, price, currency }: { onSuccess: () => void; price: number; currency: 'usd' | 'inr' | 'sgd' }) {
  const [method, setMethod] = useState<'card' | 'applepay' | 'wechat'>('card');
  const [cardNumber, setCardNumber] = useState('4242 4242 4242 4242');
  const [exp, setExp] = useState('12 / 30');
  const [cvc, setCvc] = useState('123');
  const [loading, setLoading] = useState(false);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => { setLoading(false); onSuccess(); }, 900);
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2">
        Demo mode · No Stripe key configured. Set <code>VITE_STRIPE_PUBLISHABLE_KEY</code> for real checkout.
      </div>

      <div>
        <label className="label">Payment method</label>
        <div className="grid grid-cols-3 gap-2">
          <MethodTile active={method === 'card'}     onClick={() => setMethod('card')}     icon={<CreditCard className="w-4 h-4" />} label="Card" />
          <MethodTile active={method === 'applepay'} onClick={() => setMethod('applepay')} icon={<Apple className="w-4 h-4" />}      label="Apple Pay" />
          <MethodTile active={method === 'wechat'}   onClick={() => setMethod('wechat')}   icon={<Smartphone className="w-4 h-4" />} label="WeChat Pay" />
        </div>
      </div>

      {method === 'card' && (
        <div className="space-y-3">
          <div>
            <label className="label">Card number</label>
            <input className="input" value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} inputMode="numeric" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Expires</label><input className="input" value={exp} onChange={(e) => setExp(e.target.value)} /></div>
            <div><label className="label">CVC</label><input className="input" value={cvc} onChange={(e) => setCvc(e.target.value)} /></div>
          </div>
        </div>
      )}
      {method === 'applepay' && <ApplePayMock />}
      {method === 'wechat' && <WeChatPayMock />}

      <button type="submit" disabled={loading} className="btn-primary w-full py-3">
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</> : <><Lock className="w-4 h-4" /> Pay {formatPrice(price, currency)}</>}
      </button>
    </form>
  );
}

function MethodTile({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-3 py-3 text-sm rounded-xl border transition-colors flex items-center justify-center gap-2 ${active ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-ink-200 text-ink-700 hover:bg-ink-50'}`}>
      {icon} {label}
    </button>
  );
}

function ApplePayMock() {
  return (
    <div className="p-6 bg-black text-white rounded-xl text-center">
      <Apple className="w-6 h-6 mx-auto mb-2" />
      <div className="font-medium">Tap to pay with Apple Pay</div>
      <div className="text-xs text-ink-300 mt-1">Authenticate with Face ID or Touch ID in the real app.</div>
    </div>
  );
}

function WeChatPayMock() {
  return (
    <div className="p-6 bg-emerald-50 border border-emerald-100 rounded-xl text-center">
      <div className="w-24 h-24 mx-auto bg-white rounded-md grid place-items-center border border-emerald-200 text-ink-400">QR</div>
      <div className="mt-2 text-sm text-emerald-800">Scan in the WeChat app to pay</div>
    </div>
  );
}
